import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import type { KeyboardEvent } from "react";
import { useAtom } from "jotai";

import {
  useComposition,
  createLocalComposerConfig,
  ComposingEvent,
} from "@pineseed/gui/hook/useComposition";
import { useInputActionHandlers, type InputActionHandlers } from "@pineseed/gui/hook/useInputActionHandlers";
import { useAttachments } from "@pineseed/gui/hook/useAttachments";
import {
  createAttachFromDeviceHandler,
  type FileData,
} from "../handler/attachFromDevice";
import { workspaceLayoutAtom } from "@pineseed/workspace/state/layout";
import type { ComposerEvent } from "@pineseed/spec";
import type { ComposingEventDisplay, WebSocketMessage } from "@pineseed/gui/types/composing";
export type { ComposingEventDisplay, WebSocketMessage };

export interface UseComposerAppProps {
  workspaceId: string;
  conversationId: string;
  threadId: string;
  wsUrl: string | null;
  httpUrl: string;
  isAuthenticated: boolean;
  participantId: { id: string } | null;
  onComposing?: (event: ComposingEvent) => void;
  onSubmitted: (content: string) => Promise<void>;
}

export interface UseComposerAppReturn {
  // Composer state
  textInputHeight: number;
  setTextInputHeight: (height: number) => void;
  sendOnEnter: boolean;
  setSendOnEnter: (value: boolean) => void;
  showGlobalLoading: boolean;
  events: ComposingEventDisplay[];
  typingUsers: Set<string>;

  // Composition hooks
  hasText: boolean;
  setGetTextFn: (getTextFn: () => string) => void;
  setGetContentWithTagsFn: (getContentWithTagsFn: () => string) => void;
  getContentWithTagsFn: () => string;
  setClearTextFn: (clearTextFn: () => void) => void;
  onTextChange: (newHasText: boolean) => void;
  handleSubmit: () => void;
  onEditorReady: (
    getText: () => string,
    getContentWithTags: () => string,
    clearText: () => void,
    initialHasText: boolean,
    setText: (text: string) => void,
  ) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  emitComposingEvent: (content: string) => void;
  inputActionHandlers: InputActionHandlers;
}

export function useComposerApp({
  workspaceId,
  conversationId,
  threadId,
  wsUrl,
  httpUrl,
  isAuthenticated,
  participantId,
  onSubmitted,
  onComposing,
}: UseComposerAppProps): UseComposerAppReturn {

  // Use persistent layout atom for text input height
  const [layout, setLayout] = useAtom(workspaceLayoutAtom);

  // Get persisted height
  const textInputHeight = layout.textInput.height;

  // Get min/max constraints from ENV vars (not persisted, used as limits)
  const textInputMinHeight = parseInt(
    import.meta.env.VITE_IVE_TEXT_INPUT_MIN_HEIGHT || "60",
  );
  const textInputMaxHeight = parseInt(
    import.meta.env.VITE_IVE_TEXT_INPUT_MAX_HEIGHT || "300",
  );

  // Constrained setter that enforces min/max from ENV and persists height to layout atom
  const setTextInputHeight = useCallback(
    (height: number) => {
      const constrainedHeight = Math.max(
        textInputMinHeight,
        Math.min(height, textInputMaxHeight),
      );
      setLayout({
        ...layout,
        textInput: {
          ...layout.textInput,
          height: constrainedHeight,
        },
      });
    },
    [textInputMinHeight, textInputMaxHeight, layout, setLayout],
  );
  const [draftText, setDraftText] = useState("");
  const [sendOnEnter, setSendOnEnter] = useState(true);
  const [showGlobalLoading] = useState(false);
  const [events, setEvents] = useState<ComposingEventDisplay[]>([]);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());

  // WebSocket ref for composing events
  const wsRef = useRef<WebSocket | null>(null);

  // Attachments storage integration
  const attachmentsConfig = useMemo(
    () => ({
      apiBaseUrl: `${httpUrl}/api`,
      defaultBucket: "attachments",
      enableDebugLogging: true,
      fetchOptions: { credentials: "include" as RequestCredentials },
    }),
    [httpUrl],
  );

  const { uploadFile } = useAttachments(attachmentsConfig);

  // Input action handlers integration
  const attachFromDeviceHandler = useCallback(
    createAttachFromDeviceHandler({
      onFilesAdded: async (files: FileData[]) => {
        console.log("[Composer] Uploading files:", files.length);
        for (const fileData of files) {
          try {
            const file = new File([fileData.content], fileData.name, {
              type: fileData.type,
              lastModified: fileData.lastModified,
            });
            const result = await uploadFile(file, {
              generatePreview: true,
              metadata: { workspaceId, conversationId, threadId },
            });
            console.log("[Composer] Uploaded:", result.key, result.previewUrl);
          } catch (error) {
            console.error("[Composer] Upload failed:", error);
          }
        }
      },
      enableDebugLogging: true,
    }),
    [uploadFile, workspaceId, conversationId, threadId],
  );

  const inputActionHandlers = useMemo(
    () => ({
      onAttachFromDevice: attachFromDeviceHandler,
    }),
    [attachFromDeviceHandler],
  );

  const wrappedInputActionHandlers = useInputActionHandlers(inputActionHandlers, {
    enableDebugLogging: true,
    isAuthenticated,
  });

  // WebSocket connection for real-time events
  useEffect(() => {
    if (!wsUrl) return;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log(
        "🟢 Connected to WebSocket, subscribing to composing channel",
      );
      // Subscribe to composing channel for GRIP/Pushpin
      ws.send(
        JSON.stringify({
          type: "subscribe",
          channel: "composing",
        }),
      );
    };

    ws.onclose = () => {
      console.log("🔴 Disconnected from WebSocket");
    };

    ws.onerror = (error) => {
      console.error("🔴 WebSocket error:", error);
    };

    ws.onmessage = (event) => {
      if (!participantId || !participantId.id) return;

      try {
        const message = JSON.parse(event.data);
        console.log("📝 Received WebSocket message:", message);

        if (message.type === "composing") {
          const composingEvent = message as ComposerEvent;
          console.log("🟢🟢🟢 Received composing event:", composingEvent);
          // Add to events log
          setEvents((prev) => [
            ...prev.slice(-9),
            {
              id: Date.now(),
              timestamp: new Date().toLocaleTimeString(),
              participant: composingEvent.event.participantId,
              content:
                composingEvent.event.content.substring(0, 50) +
                (composingEvent.event.content.length > 50 ? "..." : ""),
            },
          ]);

          // Update typing indicator for other users only
          if (composingEvent.event.participantId !== participantId?.id) {
            setTypingUsers((prev) =>
              new Set(prev).add(composingEvent.event.participantId),
            );
            setTimeout(() => {
              setTypingUsers((prev) => {
                const newSet = new Set(prev);
                newSet.delete(composingEvent.event.participantId);
                return newSet;
              });
            }, 3000);
          }
        }
      } catch (error) {
        console.error(
          "🔴 Failed to parse WebSocket message:",
          error,
          event.data,
        );
      }
    };

    // return () => {
    //   ws.close();
    //   wsRef.current = null;
    // };
  }, [wsUrl, participantId?.id]);

  // Memoize apiConfig to prevent infinite re-renders
  const apiConfig = useMemo(() => {
    const baseConfig = createLocalComposerConfig(httpUrl);

    // Add WebSocket composing event emission
    return {
      ...baseConfig,
      onComposing: onComposing
        ? onComposing
        : async (event: any) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              const message = JSON.stringify({
                type: "composing",
                data: event,
              });
              wsRef.current.send(message);
              console.log("📡 Sent composing event via WebSocket");
            }
          },
    };
  }, [httpUrl]);

  // Use composition hook with environment-specific config
  const {
    hasText,
    onTextChange,
    handleSubmit,
    onEditorReady,
    setGetTextFn,
    setGetContentWithTagsFn,
    getContentWithTagsFn,
    setClearTextFn,
    emitComposingEvent,
  } = useComposition({
    participantId: participantId?.id || "",
    threadId,
    conversationId,
    workspaceId,
    draftText,
    setDraftText,
    apiConfig,
    onSubmitMessage: async (content: string) => {
      console.log(
        "`useComposerApp` `onSubmitMessage` - Sent message:",
        content,
      );
      try {
        await onSubmitted(content);
      } catch (error) {
        console.error("🔴 Error calling `onSubmitted` callback:", error);
      }
    },
  });

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        console.log(
          "⮑ Key pressed",
          `shift : ${e.shiftKey}`,
          `meta : ${e.metaKey}`,
          `ctrl : ${e.ctrlKey}`,
          `alt : ${e.altKey}`,
        );
        if (
          sendOnEnter &&
          !e.shiftKey &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.altKey
        ) {
          e.preventDefault();
          handleSubmit();
        } else if (!sendOnEnter && (e.ctrlKey || e.metaKey || e.altKey)) {
          e.preventDefault();
          handleSubmit();
        }
      }
    },
    [sendOnEnter, handleSubmit],
  );

  return {
    // Composer state
    textInputHeight,
    setTextInputHeight,
    sendOnEnter,
    setSendOnEnter,
    showGlobalLoading,
    emitComposingEvent,
    events,
    typingUsers,

    // Composition hooks
    hasText,
    setGetTextFn,
    setGetContentWithTagsFn,
    getContentWithTagsFn,
    setClearTextFn,
    onTextChange,
    handleSubmit,
    onEditorReady,
    onKeyDown,
    inputActionHandlers: wrappedInputActionHandlers,
  };
}
